// src/App.js
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PollingProvider, usePolling } from './services/PollingContext';
import ProtectedRoute from './components/ProtectedRoute';

import Login from './pages/auth/Login';
import AdminLogin from './pages/auth/AdminLogin';
import Dashboard from './pages/dashboard/Dashboard';
import Newscan from './pages/redteaming/Newscan';
import Configurations from './pages/dashboard/Configurations';
import Settings from './pages/dashboard/Settings';
import Details from './pages/dashboard/Details';
import Vulnerabilities from './pages/dashboard/Vulnerabilities';
import Context from './pages/appContext/Context';
import Endpoints from './pages/redteaming/Endpoints';
import Detectors from './pages/redteaming/Detectors';
import Methods from './pages/redteaming/Methods';
import Review from './pages/redteaming/Review';
import Reports from './pages/dashboard/Reports';
import SuperAdminDashboard from './pages/dashboard/SuperAdminDashboard';
import Tenants from './pages/dashboard/Tenants';
import Onboarding from './pages/dashboard/Onboarding';
import AIProviders from './pages/dashboard/AIProviders';
import ScanAccess from './pages/dashboard/ScanAccess';
import NotAuthorized from './pages/errors/NotAuthorized';
import NotFound from './pages/errors/NotFound';
import RootRedirect from './pages/auth/RootRedirect';
import Home from './pages/appFlow/Home'
import LandingPage from './pages/appFlow/LandingPage';
import ApplicationHomepage from './pages/appFlow/ApplicationHomepage';
import AuthCallBack from './pages/auth/AuthCallBack';
import SetPassword from './pages/auth/SetPassword';
import Sysprompt from './pages/promptHardening/Sysprompt';
import ArchitectureAnalysis from './pages/architectureReview/ArchitectureAnalysis';
import GuardRailsAnalysis from './pages/guardrails/GuardRailsAnalysis';
import PrivacyPolicy from './pages/misc/PrivacyPolicy';
import ForgotPassword from './pages/auth/ForgotPassword';
import { X } from 'lucide-react';
import DynamicQuestionnaire from './pages/appContext/Questionnaire';
// import NotebookFeature from './Notebook';
import ApplicationContext from './pages/appContext/ApplicationContext';
import Governance from './pages/governance/Governance';
import AgentOps from './pages/agentOps/AgentOps';
import MCPScanViewer from './pages/mcp_scanner/McpScanner';
import McpScanDetail from './pages/mcp_scanner/McpScanDetail';
// GRC module pages — rendered inside the main app shell (see /grc routes below)
import GrcOverview from './pages/grc/GrcOverview';
import GrcPolicies from './pages/grc/Policies';
import GrcPolicyDetail from './pages/grc/PolicyDetail';
import GrcControls from './pages/grc/Controls';
import GrcControlDetail from './pages/grc/ControlDetail';
import GrcQuestionnaire from './pages/grc/Questionnaire';
import GrcQuestionnaireDetail from './pages/grc/QuestionnaireDetail';
import GrcRCMBuilder from './pages/grc/RCMBuilder';




const Notification = () => {
  const { briefNotification, closeNotification } = usePolling();

  if (!briefNotification) {
    return null;
  }

  return (
    <div
      className={`group fixed top-4 right-4 z-[9999] px-4 py-3 rounded-lg border backdrop-blur-md transform transition-all duration-300 animate-in slide-in-from-top-2 ${ 
        briefNotification.type === "success"
          ? "bg-green-500/10 border-green-500/30 text-green-400"
          : briefNotification.type === "info"
          ? "bg-blue-500/10 border-blue-500/30 text-blue-400"
          : "bg-red-500/10 border-red-500/30 text-red-400"
      }`}
    >
      <div className="flex items-center gap-2 pr-6">
        {briefNotification.type === "success" ? (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : briefNotification.type === "info" ? (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
        ) : (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
        <span className="text-sm font-medium">{briefNotification.message}</span>
      </div>
      <button onClick={closeNotification} className="absolute top-1 right-1 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <PollingProvider>
        <Notification />
        <Router>
          <Routes>
            {/* <Route path="/" element={<Navigate to="/login" replace />} /> */}
            <Route path="/" index element={<LandingPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/not-authorized" element={<NotAuthorized />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
            <Route path="/redirect" element={<RootRedirect/>} />
            <Route path='/login' element={<Login/>}/>
            <Route path='/llminate-admin' element={<AdminLogin/>}/>
            <Route path='/forgot-password' element={<ForgotPassword/>}/>
            <Route path='/auth/callback' element={<AuthCallBack/>}/>
            <Route path='/set-password' element={<SetPassword/>}/>

            {/* GRC module — a normal sidebar module rendered inside the main
                app shell (<Home/> = main Sidebar + Navbar), gated like the others.
                Internal pages keep the /grc/* URL namespace.
                ASSET-SCOPED: the sidebar surfaces GRC only after an Application is
                selected (its <li> lives in the selectedApp block in Sidebar.js), the
                same as AI BOM / Prompt Hardening. When the mock data layer is replaced,
                scope GRC queries by sessionStorage 'selectedApp' like the other modules.
                ASSETS: GRC shares the app's assets. If a GRC page needs a bitmap,
                import it from the shared src/assets/ (e.g. '../../assets/foo.png'
                from pages/grc/). Do NOT recreate a local grc/assets/ folder — it
                was a byte-for-byte duplicate of src/assets/ and has been removed. */}
            <Route element={<ProtectedRoute allowedRoles={['client_admin', 'viewer']} />}>
              <Route element={<Home />}>
                <Route path="/grc" element={<Navigate to="/grc/dashboard" replace />} />
                <Route path="/grc/dashboard" element={<GrcOverview />} />
                <Route path="/grc/framework-review" element={<GrcOverview />} />
                <Route path="/grc/policies" element={<GrcPolicies />} />
                <Route path="/grc/policies/:id" element={<GrcPolicyDetail />} />
                <Route path="/grc/controls" element={<GrcControls />} />
                <Route path="/grc/controls/:id" element={<GrcControlDetail />} />
                <Route path="/grc/questionnaires" element={<GrcQuestionnaire />} />
                <Route path="/grc/questionnaires/:id" element={<GrcQuestionnaireDetail />} />
                <Route path="/grc/rcm" element={<GrcRCMBuilder />} />
              </Route>
            </Route>


            {/* Super Admin Routes */}
            <Route element={<ProtectedRoute allowedRoles={'super_admin'} />}>
              <Route path="/admin-dashboard" element={<SuperAdminDashboard />}>
                <Route path="tenants" element={<Tenants />} />
                <Route path="onboarding" element={<Onboarding />} />
                <Route path="ai-providers" element={<AIProviders />} />
                <Route path="scan-access" element={<ScanAccess />} />
              </Route>
            </Route>

            {/* Viewer Routes */}
            <Route element={<ProtectedRoute allowedRoles={['client_admin', 'viewer']} />}>
              <Route element={<Home />}>
              {/* <Route element={<Home/>}> */}
                {/* <Route path="/home" element={<Home/>}> */}
                  <Route path="/application-dashboard" element={<ApplicationHomepage/>}/>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/appcontext" element={<ApplicationContext />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/architecture-analysis" element={<ArchitectureAnalysis />} />
                  <Route
                    element={
                      <ProtectedRoute allowedRoles={['client_admin', 'viewer']} allowedFeatures="guardRails" />
                    }
	                  >
	                    <Route path="/guardrail-analysis" element={<GuardRailsAnalysis />} />
	                  </Route>
	                  <Route
	                    element={
	                      <ProtectedRoute allowedRoles={['client_admin', 'viewer']} allowedFeatures="prompthardening" />
	                    }
	                  >
	                    <Route path="/prompt-hardening" element={<Sysprompt />} />
	                  </Route>
	                  <Route path="/details/:id" element={<Details />} />
	                  <Route path="/vulnerabilities/:filename" element={<Vulnerabilities />} />
                {/* </Route> */}
              </Route>
            </Route>
           
            <Route element={<ProtectedRoute allowedRoles={'client_admin'} />}>
              <Route element={<Home/>}>
                <Route path="/configurations" element={<Configurations />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/assessment" element={<DynamicQuestionnaire/>}/>
                <Route path="/governance" element={<Governance/>}/>
                <Route path="/agent-ops" element={<AgentOps/>}/>
                <Route path="/mcp-scan" element={<MCPScanViewer/>}/>
                <Route path="/mcp-scan/:assetName/:filename" element={<McpScanDetail/>}/>
                <Route path="/newscan" element={<Newscan />}>
                  <Route index element={<Navigate to="endpoints" replace />} />
                  <Route path="context" element={<Context />} />
                  <Route path="endpoints" element={<Endpoints />} />
                  <Route path="detectors" element={<Detectors />} />
                  <Route path="methods" element={<Methods />} />
                  <Route path="review" element={<Review />} />
                </Route>
	              </Route>
	            </Route>

            
          </Routes>
        </Router>
      </PollingProvider>
    </AuthProvider>
  );
}

export default App;
